import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@shared/components/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:pointer-events-none disabled:opacity-50 cursor-pointer select-none",
	{
		variants: {
			variant: {
				default:
					"bg-content-primary/80 hover:bg-content-primary text-white hover:bg-blend-soft-light active:bg-content-primary/50",
				outline:
					"border border-slate-600 bg-transparent hover:bg-slate-700 active:bg-slate-600 text-slate-300",
				secondary: "bg-slate-500 text-white hover:bg-slate-500 active:bg-slate-400",
				ghost: "hover:bg-slate-500 active:bg-slate-700 text-slate-300",
				destructive: "bg-red-700 text-white hover:bg-red-600 active:bg-red-500",
				link: "text-slate-300 underline-offset-4 hover:underline hover:text-white",
			},
			size: {
				default: "h-9 px-4 py-2",
				xs: "h-7 px-2 text-xs",
				sm: "h-8 px-3 text-sm",
				lg: "h-10 px-8 text-base",
				icon: "p-2 h-9 w-9",
				"icon-xs": "h-7 w-7",
				"icon-sm": "h-8 w-8",
				"icon-lg": "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

interface ButtonProps extends VariantProps<typeof buttonVariants> {}

const Button: React.FC<
	ButtonProps &
		React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
> = (props) => {
	const { variant, size, className, ...rest } = props;
	return (
		<button className={cn(buttonVariants({ variant, size, className }))} {...rest}>
			{props.children}
		</button>
	);
};

export { Button };
